import Axios from "axios";

export interface TtsGenerationVoiceData {
  addedAt: Date;
  age?: TtsGenerationAge;
  name: string;
  notes: string;
  gender?: TtsGenerationGender;
  id: string;
  isExpressive: boolean;
  isMultilingual: boolean;
  languageId: string;
  languageStatuses: { language: string; qa_status: string }[];
  character?: TtsGenerationCharacter;
  provider: TtsGenerationProvider;
  providerParameters?: Record<string, string | number | boolean | null>;
  alias: string;
  duolingoLanguageId?: string;
  status: TtsGenerationStatus;
}

export interface TtsGenerationRequest {
  languageId: string;
  voiceName: string;
  text: string;
  ssml?: string;
  isRawSsml?: boolean;
  breakTime?: number;
  prosody?: number;
}

export type TtsGenerationAge =
  | "ADULT"
  | "CHILD"
  | "ELDERLY"
  | "MATURE"
  | "TEEN";
export type TtsGenerationCharacter =
  | "CHARACTER_UNKNOWN"
  | "BEA"
  | "EDDY"
  | "JUNIOR"
  | "LILY"
  | "LIN"
  | "LUCY"
  | "OSCAR"
  | "VIKRAM"
  | "ZARI"
  | "FALSTAFF";
export type TtsGenerationGender =
  | "GENDER_UNKNOWN"
  | "MALE"
  | "FEMALE"
  | "OTHER";
export type TtsGenerationProvider =
  | "AMAZON"
  | "AZURE"
  | "ELEVENLABS"
  | "GOOGLE"
  | "PROVIDER_UNKNOWN";
export type TtsGenerationStatus =
  | "ACTIVE"
  | "DEPRECATED"
  | "DRAFT"
  | "UNAVAILABLE";
export type TtsGenerationCustomVoiceEndpointStatus =
  | "SUSPENDED"
  | "SPINNING_DOWN"
  | "SPINNING_UP"
  | "ACTIVE"
  | "UNKNOWN";

const ttsGenerationClient = Axios.create({
  baseURL: `${process.env.REACT_APP_TTS_GENERATION_ORIGIN}`,
  withCredentials: true,
});

// v2 voices API response item (subset of fields we need)
interface VoicesV2Item {
  added_at: string;
  age: string;
  id: string;
  is_expressive?: boolean;
  is_multilingual?: boolean;
  language_statuses: { language: string; qa_status: string }[];
  name: string;
  notes: string;
  gender: string;
  primary_language: string;
  provider: string;
  provider_parameters?: {
    model_name?: string;
  } | null;
  status: string;
  world_character?: string | null;
}

const getTtsGenerationVoices = async () => {
  const v2 = (await ttsGenerationClient.get<VoicesV2Item[]>("/v2/voices")).data;
  const mapped: TtsGenerationVoiceData[] = v2.map(item => ({
    addedAt: new Date(item.added_at),
    age: item.age?.toUpperCase() as TtsGenerationAge,
    alias: item.name,
    character: item.world_character?.toUpperCase() as TtsGenerationCharacter,
    duolingoLanguageId: item.primary_language,
    gender: item.gender?.toUpperCase() as TtsGenerationGender,
    id: item.id,
    isExpressive: !!item.is_expressive,
    isMultilingual: !!item.is_multilingual,
    languageId: item.primary_language,
    languageStatuses: item.language_statuses,
    name: item.provider_parameters?.model_name ?? "",
    notes: item.notes,
    provider: item.provider.toUpperCase() as TtsGenerationProvider,
    providerParameters: item.provider_parameters ?? undefined,
    status: item.status.toUpperCase() as TtsGenerationStatus,
  }));
  return Object.fromEntries(mapped.map(voice => [voice.name, voice]));
};

let ttsGenerationVoicesMemo: Record<string, TtsGenerationVoiceData>;
export const getTtsGenerationVoiceList = async () => {
  if (!ttsGenerationVoicesMemo) {
    ttsGenerationVoicesMemo = await getTtsGenerationVoices();
  }

  return Object.values(ttsGenerationVoicesMemo);
};

export const getTtsGenerationVoice = async (
  voiceName: string,
): Promise<TtsGenerationVoiceData | undefined> => {
  if (!ttsGenerationVoicesMemo) {
    ttsGenerationVoicesMemo = await getTtsGenerationVoices();
  }

  return ttsGenerationVoicesMemo[voiceName];
};

export const getLanguages = async () =>
  (
    await ttsGenerationClient.get<
      {
        bcp47: string;
        default_dialect: boolean;
        duo_code: string;
        duo_id: string;
        example_sentence: string;
        iso1: string;
        iso3: string;
        name: string;
        scripts: string[];
        whitespace_delimited: boolean;
      }[]
    >("/v1/languages")
  ).data;

export const getCustomVoiceEndpointStatus = async (voiceName: string) =>
  (await getCustomVoiceEndpointStatuses([voiceName]))[voiceName];

export const getCustomVoiceEndpointStatuses = async (voiceNames: string[]) =>
  (
    await ttsGenerationClient.get<
      Record<string, TtsGenerationCustomVoiceEndpointStatus>
    >("/endpoint/statuses", {
      params: {
        voiceNames: voiceNames.join(","),
      },
    })
  ).data;

export const spinUpCustomVoiceEndpoint = (voiceName: string) =>
  spinUpCustomVoiceEndpoints([voiceName]);

export const spinUpCustomVoiceEndpoints = (voiceNames: string[]) =>
  ttsGenerationClient.post<"Accepted">(
    "/endpoint/spin-up",
    {
      voiceNames,
    },
    { validateStatus: status => status === 202 },
  );

export const getTtsGenerationAudio = async (
  requestData: TtsGenerationRequest,
) =>
  (
    await ttsGenerationClient.post<Blob>(
      "/tts/sync/file",
      {
        language_id: requestData.languageId,
        texts: [
          {
            break_time: requestData.breakTime,
            content: requestData.text,

            prosody_rate: requestData.prosody,
            raw_ssml: requestData.isRawSsml ? requestData.ssml : undefined,
            ssml_body: requestData.isRawSsml ? undefined : requestData.ssml,
          },
        ],
        voice_names: [requestData.voiceName],
      },
      {
        headers: {
          Accept: "application/json",
        },
        responseType: "blob",
      },
    )
  ).data;
